import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, fromEvent } from 'rxjs';
import { filter, pairwise, startWith } from 'rxjs/operators';
import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel';
import { AppStore } from '@app/core/states';
import { Events } from '@ngrx/signals/events';
import { DataSyncEvents } from '@app/domain/events';
import { Injectable } from '@angular/core';
import { RUNTIME_KIND, type RuntimeKind } from '@app/core/tokens/infrastructure.tokens';

export enum TabSyncMessageType {
  DATABASE_CHANGED = 'database-changed',
  AUTH_LOCKED = 'auth-locked',
  RELOAD_REQUEST = 'reload-request',
}

export type AuthLockedReason = 'manual' | 'inactivity' | 'security';

export interface AuthLockedPayload {
  reason: AuthLockedReason;
}

export interface TabSyncMessage {
  type: TabSyncMessageType;
  timestamp: number;
  payload?: unknown;
}

/**
 * Multi-tab synchronization service using BroadcastChannel with leader election.
 * Coordinates database writes, authentication state, and data synchronization across browser tabs.
 */
@Injectable({ providedIn: 'root' })
export class MultiTabSyncService {
  private readonly appStore = inject(AppStore);
  private readonly events = inject(Events);
  private readonly destroyRef = inject(DestroyRef);
  private readonly runtimeKind = inject<RuntimeKind>(RUNTIME_KIND);
  private readonly channel: BroadcastChannel<TabSyncMessage> | null = null;
  private readonly elector: LeaderElector | null = null;
  private readonly messages$: Observable<TabSyncMessage>;
  private isDestroyed = false;

  /** Reason for the next AUTH_LOCKED broadcast. Defaults to 'manual'. */
  private pendingLockReason: AuthLockedReason = 'manual';

  constructor() {
    if (this.runtimeKind === 'capacitor') {
      console.log('[MultiTabSync] Native runtime detected, using single-instance sync mode');
      this.appStore.setIsLeader(true);
      this.messages$ = EMPTY;
      this.destroyRef.onDestroy(() => {
        this.cleanup();
      });
      return;
    }

    this.channel = new BroadcastChannel<TabSyncMessage>('finance-app-sync');
    this.elector = createLeaderElection(this.channel);

    // Listen for leadership changes
    this.elector
      .awaitLeadership()
      .then(() => {
        if (this.isDestroyed) {
          return;
        }

        console.log('[MultiTabSync] This tab became the leader');
        this.appStore.setIsLeader(true);
      })
      .catch((error) => {
        if (!this.isDestroyed) {
          console.error('Failed to elect tab leader:', error);
        }
      });

    // Create observable for incoming messages
    this.messages$ = new Observable<TabSyncMessage>((subscriber) => {
      const channel = this.channel;
      if (!channel) {
        subscriber.complete();
        return;
      }
      const handler = (msg: TabSyncMessage) => subscriber.next(msg);
      channel.addEventListener('message', handler);
      return () => channel.removeEventListener('message', handler);
    });

    // Log leadership status on page visibility change
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (!document.hidden) {
            console.log('[MultiTabSync] Tab visible, isLeader:', this.appStore.isLeader());
          }
        });
    }

    // Watch for auth lock events and broadcast to other tabs
    toObservable(this.appStore.isUnlocked)
      .pipe(
        startWith(this.appStore.isUnlocked()),
        pairwise(),
        filter(([prev, curr]) => prev && !curr), // Detect transition from unlocked to locked
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const reason = this.pendingLockReason;
        this.pendingLockReason = 'manual';
        this.notifyAuthLocked(reason).catch((error) => {
          console.error('Failed to broadcast auth lock:', error);
        });
      });

    // Listen for database sync events and broadcast to other tabs
    this.events
      .on(DataSyncEvents.syncCompleted)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notifyDatabaseChanged().catch((error) => {
          console.error('Failed to broadcast database sync:', error);
        });
      });

    // Register cleanup callback
    this.destroyRef.onDestroy(() => {
      this.cleanup();
    });
  }

  /**
   * Listen for messages of a specific type.
   */
  onMessage(type: TabSyncMessageType): Observable<TabSyncMessage> {
    return this.messages$.pipe(filter((msg) => msg.type === type));
  }

  /**
   * Listen for all messages.
   */
  onAllMessages(): Observable<TabSyncMessage> {
    return this.messages$;
  }

  /**
   * Request all tabs to reload their data.
   */
  async requestReload(): Promise<void> {
    await this.broadcast(TabSyncMessageType.RELOAD_REQUEST);
  }

  /**
   * Mark the reason for the next auth-lock broadcast.
   * Must be called before the action that transitions `isUnlocked` to `false`
   * so the correct reason is included in the AUTH_LOCKED message sent to other tabs.
   *
   * The reason resets to `'manual'` automatically after each broadcast.
   */
  setPendingLockReason(reason: AuthLockedReason): void {
    this.pendingLockReason = reason;
  }

  /**
   * Notify other tabs that the database has changed.
   */
  private async notifyDatabaseChanged(payload?: { tables?: string[] }): Promise<void> {
    await this.broadcast(TabSyncMessageType.DATABASE_CHANGED, payload);
  }

  /**
   * Notify other tabs that authentication has been locked (logout).
   */
  private async notifyAuthLocked(reason: AuthLockedReason): Promise<void> {
    const payload: AuthLockedPayload = { reason };
    await this.broadcast(TabSyncMessageType.AUTH_LOCKED, payload);
  }

  /**
   * Broadcast a message to all other tabs (excluding this one).
   */
  private async broadcast(type: TabSyncMessageType, payload?: unknown): Promise<void> {
    if (!this.channel) {
      return;
    }

    const message: TabSyncMessage = {
      type,
      timestamp: Date.now(),
      payload,
    };
    await this.channel.postMessage(message);
  }

  private cleanup(): void {
    this.isDestroyed = true;
    this.appStore.setIsLeader(false);
    this.elector?.die().catch(console.error);
    this.channel?.close().catch(console.error);
  }
}
